import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { useStateMachine } from "@/core/arch/state_machine";
import { ReferralsBloc } from "@/application/referrals/referrals_bloc";
import { ReferralsRepository } from "@/application/referrals/referrals_repository";
import type {
  ReferralStage,
  ReferredUserRow,
} from "@/application/referrals/types";
import { StageBadge } from "@/presentation/merchants/components/StageBadge";

const STAGE_OPTIONS: { value: ReferralStage; label: string }[] = [
  { value: "not_referred", label: "Not referred" },
  { value: "signed_up", label: "Signed up" },
  { value: "qualifying", label: "Qualifying" },
  { value: "eligible", label: "Eligible" },
  { value: "rewarded", label: "Rewarded" },
  { value: "ineligible", label: "Ineligible" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ReferralsView() {
  const navigate = useNavigate();
  const [state, bloc] = useStateMachine(
    () => new ReferralsBloc(new ReferralsRepository()),
  );

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const filteredItems = useMemo(() => {
    if (state.stageFilter.length === 0) return state.items;
    const set = new Set(state.stageFilter);
    return state.items.filter((row) => set.has(row.referralStage));
  }, [state.items, state.stageFilter]);

  const columns: ColumnDef<ReferredUserRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "email",
        header: "Referee email",
        cell: ({ row }) => {
          const email = row.original.email;
          const businessId = row.original.businessId;
          if (businessId) {
            return (
              <button
                className="text-blue-600 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    ROUTES.MERCHANT_DETAIL.replace(":businessId", businessId),
                  );
                }}
              >
                {email}
              </button>
            );
          }
          return <span>{email}</span>;
        },
      },
      {
        accessorKey: "businessName",
        header: "Business",
        cell: ({ row }) => (
          <span className="text-foreground text-sm">
            {row.original.businessName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "referrerName",
        header: "Referrer",
        cell: ({ row }) => (
          <span className="text-foreground text-sm">
            {row.original.referrerName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "referralCode",
        header: "Code",
        cell: ({ row }) => (
          <button
            className="text-muted-foreground font-mono text-xs hover:text-blue-600"
            onClick={(e) => {
              e.stopPropagation();
              void navigator.clipboard.writeText(row.original.referralCode);
            }}
            title="Copy referral code"
          >
            {row.original.referralCode}
          </button>
        ),
      },
      {
        accessorKey: "referralStage",
        header: "Stage",
        cell: ({ row }) => (
          <StageBadge
            stage={row.original.referralStage}
            hasError={row.original.payoutStatus === "failed"}
          />
        ),
      },
      {
        accessorKey: "userCreatedAt",
        header: "Signed up",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(row.original.userCreatedAt)}
          </span>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-foreground text-2xl font-bold">Referrals</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Users with a captured referral code, joined to their referral track
          stage.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {STAGE_OPTIONS.map((opt) => {
          const active = state.stageFilter.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => {
                const next = active
                  ? state.stageFilter.filter((s) => s !== opt.value)
                  : [...state.stageFilter, opt.value];
                bloc.setStageFilter(next);
              }}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                active
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-border bg-card text-muted-foreground hover:bg-muted",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
        {state.stageFilter.length > 0 && (
          <button
            onClick={() => bloc.setStageFilter([])}
            className="text-muted-foreground hover:text-foreground rounded-full px-3 py-1 text-xs font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {state.status === "loading" && state.items.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : state.status === "error" && state.items.length === 0 ? (
        <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
          Failed to load referrals: {state.error}
        </div>
      ) : (
        <>
          {state.status === "error" && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
              Failed to load more referrals: {state.error}
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredItems}
            onRowClick={(row) => {
              if (row.businessId) {
                navigate(
                  ROUTES.MERCHANT_DETAIL.replace(":businessId", row.businessId),
                );
              }
            }}
          />
          {state.nextToken && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  void bloc.loadMore();
                }}
                disabled={state.status === "loading"}
              >
                {state.status === "loading" && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
